import type { CaptureResult } from 'posthog-js'
import { apiClient } from '@/lib/api/api-client'
import { useOrganizationStore } from '@/store/organization.store'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

import type { User } from '@scrambl/shared'

type AnalyticsUser = Pick<User, 'id' | 'email' | 'role' | 'createdAt'> & {
  displayName?: string | null
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  'https://us.i.posthog.com'
const APP_URL =
  (import.meta.env.VITE_APP_URL as string | undefined) ??
  'http://localhost:3005'
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const APP_ENV =
  (import.meta.env.VITE_APP_ENV as string | undefined) ??
  (import.meta.env.PROD ? 'production' : 'development')
const APP_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined
const POSTHOG_CAPTURE =
  APP_ENV === 'production' ||
  (
    import.meta.env.VITE_POSTHOG_CAPTURE as string | undefined
  )?.toLowerCase() === 'true'
const IS_TAURI = typeof window !== 'undefined' && '__TAURI__' in window
const APP_PLATFORM = IS_TAURI ? 'desktop' : 'web'
const APP_RUNTIME = IS_TAURI ? 'tauri' : 'browser'

let posthogReady = false
let sentryReady = false

// Event catalog — the canonical list of custom events the app emits.
// PostHog autocapture handles pageviews, clicks, and form submits; these
// cover product moments autocapture can't infer cleanly.
export type EventName =
  | 'feature_used'
  | 'module_opened'
  | 'task_created'
  | 'task_completed'
  | 'project_created'
  | 'benny_message_sent'
  | 'simple_escape'
  | 'export_generated'
  | 'invite_sent'
  | 'help_article_viewed'
  | 'help_search_performed'
  | 'help_feedback_submitted'
  | 'scenario_created'
  | 'scenario_calculated'
  | 'scenario_comparison_opened'
  | 'scenario_results_viewed'
  | 'onboarding_flow_started'
  | 'onboarding_step_completed'
  | 'onboarding_step_skipped'
  | 'onboarding_import_completed'
  | 'onboarding_flow_completed'
  // Activation funnel — a single event carrying a `milestone` property so the
  // whole chain is ONE PostHog funnel insight instead of six separate events.
  | 'activation_milestone'

export interface EventProperties {
  // Free-form; keep keys stable and snake_case.
  [key: string]: string | number | boolean | null | undefined
}

// The agency activation chain, in funnel order. Emitted as
// `activation_milestone` with `milestone` set to one of these.
export const ACTIVATION_MILESTONES = [
  'client_added',
  'project_created',
  'task_created',
  'teammate_invited',
  'time_logged',
  'invoice_sent',
] as const

export type ActivationMilestone = (typeof ACTIVATION_MILESTONES)[number]

// Deliberately small allowlist for `feature_used`. Adding a member here is a
// product decision — this event is for features whose ADOPTION we report on,
// not a catch-all interaction log.
export type TrackedFeature =
  | 'bulk_task_status'
  | 'project_template'
  | 'time_timer'
  | 'data_import'
  | 'benny_action_plan'
  | 'client_portal_invite'

function getAnalyticsUrl(path?: string): string {
  const fallbackPath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/'

  try {
    return new URL(path ?? fallbackPath, APP_URL).toString()
  } catch {
    return path ?? fallbackPath
  }
}

function getAnalyticsSuperProperties(): EventProperties {
  return {
    app_platform: APP_PLATFORM,
    app_runtime: APP_RUNTIME,
    app_environment: APP_ENV,
    app_version: APP_VERSION,
  }
}

function normalizeDesktopEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event || !IS_TAURI) return event

  const path =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : undefined
  const analyticsUrl = getAnalyticsUrl(path)

  let host: string | undefined
  try {
    host = new URL(analyticsUrl).host
  } catch {
    host = undefined
  }

  return {
    ...event,
    properties: {
      ...event.properties,
      ...getAnalyticsSuperProperties(),
      $current_url: analyticsUrl,
      $host: host,
      $pathname:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
    },
  }
}

export function initAnalytics(): void {
  if (POSTHOG_KEY && !posthogReady) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      api_transport: 'fetch',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      persistence: IS_TAURI ? 'localStorage' : 'localStorage+cookie',
      cross_subdomain_cookie: !IS_TAURI,
      session_recording: {
        // Explicit production budget; override when investigating a rollout.
        sampleRate: (() => {
          const value = Number(
            import.meta.env.VITE_POSTHOG_REPLAY_SAMPLE_RATE ?? '0.1',
          )
          return Number.isFinite(value) && value >= 0 && value <= 1
            ? value
            : 0.1
        })(),
        maskAllInputs: true,
        maskTextSelector: '.ph-mask',
      },
      disable_session_recording: false,
      before_send: normalizeDesktopEvent,
      loaded: ph => {
        if (!POSTHOG_CAPTURE) {
          ph.opt_out_capturing()
        } else {
          ph.opt_in_capturing()
        }
      },
    })
    posthog.register(getAnalyticsSuperProperties())
    posthogReady = true
  }

  if (SENTRY_DSN && !sentryReady) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: APP_ENV,
      release: APP_VERSION,
      tracesSampleRate: APP_ENV === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      // Replays are what turn an unreproducible bug report into something we
      // can watch. Text and media are masked because these sessions carry real
      // customer data.
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
    })
    sentryReady = true
  }
}

export function identify(user: AnalyticsUser, orgId: string | null): void {
  if (!user?.id) return

  if (posthogReady) {
    posthog.identify(user.id, {
      email: user.email,
      name: user.displayName,
      role: user.role,
      created_at: user.createdAt,
    })
    if (orgId) {
      posthog.group('organization', orgId)
    }
  }

  if (sentryReady) {
    Sentry.setUser({ id: user.id, email: user.email })
    if (orgId) {
      Sentry.setTag('organization_id', orgId)
    }
  }
}

export function resetAnalytics(): void {
  if (posthogReady) {
    posthog.reset()
  }
  if (sentryReady) {
    Sentry.setUser(null)
  }
  clearActivationMilestones()
}

export function track(event: EventName, properties?: EventProperties): void {
  if (posthogReady) {
    posthog.capture(event, {
      ...getAnalyticsSuperProperties(),
      ...properties,
    })
  }
  void sendServerEvent(event, properties)
}

async function sendServerEvent(
  event: string,
  properties?: EventProperties,
): Promise<void> {
  try {
    await apiClient('/user-events/track', {
      method: 'POST',
      body: { eventType: event, payload: properties ?? {} },
      // Onboarding can emit events before a workspace exists. Once selected,
      // use the same workspace, partner and desktop authentication as the app.
      skipOrgContext: !useOrganizationStore.getState().currentOrganization?.id,
      silentAuthFailure: true,
      timeoutMs: 5_000,
      keepalive: true,
    })
  } catch {
    // best-effort — never block user flow on telemetry
  }
}

// Cheap per-session suppression so a burst of the same milestone (e.g. three
// tasks created in a row) doesn't drown the funnel. Deliberately naive: the
// authoritative "first time ever" stamp is server-side, not here.
const ACTIVATION_SESSION_KEY = 'scrambl.activation_milestones'
const firedMilestones = new Set<string>()
let firedMilestonesRestored = false

function restoreFiredMilestones(): void {
  if (firedMilestonesRestored) return
  firedMilestonesRestored = true
  if (typeof window === 'undefined') return
  try {
    const raw = window.sessionStorage.getItem(ACTIVATION_SESSION_KEY)
    if (!raw) return
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      parsed.forEach(value => {
        if (typeof value === 'string') firedMilestones.add(value)
      })
    }
  } catch {
    // Storage unavailable or corrupt — fall back to in-memory only.
  }
}

function persistFiredMilestones(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      ACTIVATION_SESSION_KEY,
      JSON.stringify([...firedMilestones]),
    )
  } catch {
    // Storage unavailable — in-memory suppression still applies.
  }
}

function clearActivationMilestones(): void {
  firedMilestones.clear()
  firedMilestonesRestored = true
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(ACTIVATION_SESSION_KEY)
  } catch {
    // Nothing to do — the in-memory set is already cleared.
  }
}

/**
 * Emit one step of the activation funnel. Fires at most once per milestone per
 * browser session; call it freely from every authoritative moment.
 */
export function trackActivationMilestone(
  milestone: ActivationMilestone,
  properties?: EventProperties,
): void {
  restoreFiredMilestones()
  if (firedMilestones.has(milestone)) return
  firedMilestones.add(milestone)
  persistFiredMilestones()

  track('activation_milestone', { ...properties, milestone })
}

/** Emit `feature_used` for one of the adoption-tracked features. */
export function trackFeatureUsed(
  feature: TrackedFeature,
  properties?: EventProperties,
): void {
  track('feature_used', { ...properties, feature })
}

export function trackPageview(path: string): void {
  if (!posthogReady) return
  posthog.capture('$pageview', {
    ...getAnalyticsSuperProperties(),
    $current_url: getAnalyticsUrl(path),
  })
}
